/**
 * WHERE A CLIENT REVIEW READS AND WRITES.
 *
 * The client review surface (the month's posts and briefs as the client sees
 * them) is served to a logged-in Guest — a brand's client in the app —
 * authenticated by their session and scoped by brand + month in the path
 * (`/guest-calendar/...`, see `guestPortalSource`). The same hooks and
 * components also render the team's client preview and the design harness from
 * other sources, so a source is just "how to reach the resources". `id` keys
 * the React Query cache: one month, one viewer.
 */
import type { QueryClient } from "@tanstack/react-query";
import type { PortalReviewDecision } from "@/types/clientPortal.type";
import type { ClientBriefsMonth } from "./useClientBriefs";

export type PortalResource = "context" | "calendar" | "briefs";

/** The month's sent briefs, keyed by the source alone like the rest of the portal. */
export const clientBriefsKey = (token: string) => ["clientPortal", "briefs", token];

/**
 * Write one post's brief answer into the cache at once. The client answers a
 * post once, idea and brief together, so the idea's answer lands here too
 * whenever its brief was waiting with it. Returns the month as it was, for the
 * rollback; undefined when nothing changed.
 */
export function markBriefDecided(
  qc: QueryClient,
  token: string,
  answer: { slotId: string; decision: PortalReviewDecision; reason: string | null; clientName: string },
  only: "awaiting" | "any" = "any",
): ClientBriefsMonth | undefined {
  const previous = qc.getQueryData<ClientBriefsMonth>(clientBriefsKey(token));
  const target = previous?.briefs.find((b) => b.slotId === answer.slotId);
  if (!previous || !target || (only === "awaiting" && !(target.phase === "awaiting_you" && target.actionable))) {
    return undefined;
  }
  qc.setQueryData<ClientBriefsMonth>(clientBriefsKey(token), {
    ...previous,
    briefs: previous.briefs.map((b) =>
      b.slotId === answer.slotId
        ? {
            ...b,
            phase: "decided" as const,
            actionable: false,
            decision: {
              decision: answer.decision,
              reason: answer.reason,
              clientName: answer.clientName,
              decidedAt: new Date().toISOString(),
              carriedOver: false,
            },
          }
        : b,
    ),
  });
  return previous;
}

export type PortalSource = {
  id: string;
  get: <T>(resource: PortalResource) => Promise<{ data: T }>;
  post: <T>(path: string, body: unknown) => Promise<{ data: T }>;
  /** An error after which polling stops (the grant is gone). */
  isDead: (error: unknown) => boolean;
};

/**
 * The team's client preview: it renders the client's panels from the team's
 * own data and never reads or writes through a source. Anything that tries is
 * a bug, so it fails loudly.
 */
export const PREVIEW_PORTAL_SOURCE: PortalSource = {
  id: "client-preview",
  get: () => Promise.reject(new Error("The client preview has no client data source.")),
  post: () => Promise.reject(new Error("The client preview is read-only.")),
  isDead: () => true,
};
